/**
 * Location login data access.
 *
 * Every mutation invalidates the location's own login query and the business's
 * list, because the Locations page shows whether each location has one.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateLocationLoginInput,
  LocationLoginView,
  UpdateLocationLoginInput,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

export function useLocationLogins(branchId: string): UseQueryResult<{ data: LocationLoginView[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['locationLogins', path(''), branchId],
    queryFn: () => api.get<{ data: LocationLoginView[] }>(path(`/branches/${branchId}/logins`)),
    staleTime: 30_000,
  });
}

/** Every location login in the business, for the Locations index. */
export function useAllLocationLogins(enabled: boolean): UseQueryResult<{ data: LocationLoginView[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['locationLogins', path(''), 'all'],
    queryFn: () => api.get<{ data: LocationLoginView[] }>(path('/location-logins')),
    enabled,
    staleTime: 60_000,
  });
}

function useInvalidate(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['locationLogins'] });
  };
}

export function useCreateLocationLogin(
  branchId: string,
): UseMutationResult<{ data: LocationLoginView }, Error, CreateLocationLoginInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input) =>
      api.post<{ data: LocationLoginView }>(path(`/branches/${branchId}/logins`), input),
    onSuccess: invalidate,
  });
}

export function useUpdateLocationLogin(
  branchId: string,
  id: string,
): UseMutationResult<{ data: LocationLoginView }, Error, UpdateLocationLoginInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input) =>
      api.patch<{ data: LocationLoginView }>(path(`/branches/${branchId}/logins/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useResetLocationPassword(
  branchId: string,
  id: string,
): UseMutationResult<{ sessionsRevoked: number }, Error, string> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (password) =>
      api.post<{ sessionsRevoked: number }>(
        path(`/branches/${branchId}/logins/${id}/password`),
        { password },
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteLocationLogin(
  branchId: string,
  id: string,
): UseMutationResult<unknown, Error, void> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api.del(path(`/branches/${branchId}/logins/${id}`)),
    onSuccess: invalidate,
  });
}
